import { IPagination } from 'src/app/shared/models/page.model';
import { IPriceBookSimple } from '../models/pricebook.model';
import { Action, createReducer, on } from '@ngrx/store';
import * as pricebookActions from './pricebook.actions';

export interface PricebooksState {
  metadata: IPagination | null;
  pricebooks: IPriceBookSimple[];
  pricebook: IPriceBookSimple | null;
  loading: boolean;
}

export const initialPricebooksState: PricebooksState = {
  metadata: null,
  pricebooks: [],
  pricebook: null,
  loading: false,
};

const featureReducer = createReducer(
  initialPricebooksState,
  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║                                                                    ║
   * ║                    Pricebook Reducers                              ║
   * ║                                                                    ║
   * ║   All reducers related to pricebook management in the application. ║
   * ║   Includes loading pricebooks.                                     ║
   * ║                                                                    ║
   * ╚════════════════════════════════════════════════════════════════════╝
   */
  on(pricebookActions.GetPricebooksSuccess, (state, action) => ({
    ...state,
    pricebooks: action.pricebooks.data,
    metadata: action.pricebooks.metadata
  })),
  on(pricebookActions.AppendPricebooksSuccess, (state, action) => ({
    ...state,
    pricebooks: [...state.pricebooks, ...action.pricebooks.data],
    metadata: action.pricebooks.metadata
  })),
  on(pricebookActions.CreatePriceBookSuccess, (state, action) => {
    // If not all available products are shown (there are more items), remove the last item
    const shouldRemoveLastItem = state.metadata && state.pricebooks.length < state.metadata.count;
    const updatedPricebooks = shouldRemoveLastItem 
      ? [action.pricebook, ...state.pricebooks.slice(0, -1)]
      : [action.pricebook, ...state.pricebooks];
    
    return {
      ...state,
      pricebooks: updatedPricebooks,
      metadata: state.metadata ? { ...state.metadata, count: state.metadata.count + 1 } : null
    };
  }),
  on(pricebookActions.SetPricebookLoader, (state, action) => ({
    ...state,
    loading: action.isLoading
  })),
  on(pricebookActions.UpdatePriceBookSuccess, (state, action) => ({
    ...state,
    pricebooks: state.pricebooks.map(pricebook => pricebook.id === action.pricebook.id ? action.pricebook : pricebook)
  })),
  on(pricebookActions.DeletePricebookSuccess, (state, action) => ({
    ...state,
    pricebooks: state.pricebooks.filter(pricebook => pricebook.id !== action.id),
    metadata: state.metadata ? { ...state.metadata, count: Math.max(0, state.metadata.count - 1) } : null
  })),
  on(pricebookActions.BulkDeletePricebooksSuccess, (state, action) => ({
    ...state,
    pricebooks: state.pricebooks.filter(pricebook => !action.ids.includes(pricebook.id)),
    metadata: state.metadata ? { ...state.metadata, count: Math.max(0, state.metadata.count - action.ids.length) } : null
  })),
  on(pricebookActions.EnablePricebooksSuccess, (state, action) => ({
    ...state,
    pricebooks: state.pricebooks.map(pricebook => action.ids.includes(pricebook.id) ? { ...pricebook, active: true } : pricebook)
  })),
  on(pricebookActions.DisablePricebooksSuccess, (state, action) => ({
    ...state,
    pricebooks: state.pricebooks.map(pricebook => action.ids.includes(pricebook.id) ? { ...pricebook, active: false } : pricebook)
  }))
);

export function pricebooksReducer(state: PricebooksState | undefined, action: Action) {
  return featureReducer(state, action);
}
